import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  NumericParameters,
  OptionParameters,
} from '@epicgames-ps/lib-pixelstreamingfrontend-ue5.5';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { StreamingProviders } from '@core/dashboard/providers/MytaverseEventProvider/interfaces';
import { useStreamStats } from './useStreamStats';
import { useStyles } from './styles';

const StatRow = ({
  label,
  value,
  classes,
}: {
  label: string;
  value: string;
  classes: ReturnType<typeof useStyles>;
}) => (
  <>
    <Typography sx={classes.statLabel}>{label}</Typography>
    <Typography sx={classes.statValue}>{value}</Typography>
  </>
);

const formatValue = (value: number | string | null, suffix = '') =>
  value === null || value === undefined ? '---' : `${value}${suffix}`;

const PixelStreamingTab = () => {
  const { streamingProvider, mytaPixelStreaming } = useMytaverseEvent();
  const classes = useStyles();
  const stats = useStreamStats(mytaPixelStreaming);

  const [minBitrate, setMinBitrate] = useState('');
  const [maxBitrate, setMaxBitrate] = useState('');
  const [codecOptions, setCodecOptions] = useState<string[]>([]);
  const [selectedCodec, setSelectedCodec] = useState('');

  useEffect(() => {
    if (!mytaPixelStreaming) {
      return;
    }

    const { config } = mytaPixelStreaming;

    setMinBitrate(
      String(config.getNumericSettingValue(NumericParameters.WebRTCMinBitrate)),
    );
    setMaxBitrate(
      String(config.getNumericSettingValue(NumericParameters.WebRTCMaxBitrate)),
    );

    const preferredCodec = config.getSettingOption(OptionParameters.PreferredCodec);
    setCodecOptions(preferredCodec.options);
    setSelectedCodec(preferredCodec.selected);
  }, [mytaPixelStreaming]);

  if (streamingProvider !== StreamingProviders.MytaStreams) {
    return (
      <Box sx={classes.root} className="no-drag">
        <Typography sx={classes.placeholder}>
          Pixel Streaming debug tools are only available for the MYTA_STREAMS
          provider (current: {streamingProvider || '---'}).
        </Typography>
      </Box>
    );
  }

  if (!mytaPixelStreaming) {
    return (
      <Box sx={classes.root} className="no-drag">
        <Typography sx={classes.placeholder}>
          Waiting for the MYTA_STREAMS connection to be established...
        </Typography>
      </Box>
    );
  }

  const applyBitrate = () => {
    const { config } = mytaPixelStreaming;
    const min = Number(minBitrate);
    const max = Number(maxBitrate);

    if (Number.isFinite(min)) {
      config.setNumericSetting(NumericParameters.WebRTCMinBitrate, min);
    }

    if (Number.isFinite(max)) {
      config.setNumericSetting(NumericParameters.WebRTCMaxBitrate, max);
    }
  };

  const handleCodecChange = (value: string) => {
    setSelectedCodec(value);
    mytaPixelStreaming.config.setOptionSettingValue(
      OptionParameters.PreferredCodec,
      value,
    );
  };

  return (
    <Box sx={classes.root} className="no-drag">
      {stats.turnRelayed && (
        <Typography sx={classes.badge}>Relayed via TURN</Typography>
      )}

      <Box sx={classes.section}>
        <Typography sx={classes.sectionTitle}>Live stats</Typography>
        <Box sx={classes.statsGrid}>
          <StatRow label="FPS" value={formatValue(stats.fps)} classes={classes} />
          <StatRow
            label="Bitrate"
            value={formatValue(stats.bitrateKbps, ' kbps')}
            classes={classes}
          />
          <StatRow
            label="Resolution"
            value={
              stats.width && stats.height
                ? `${stats.width}x${stats.height}`
                : '---'
            }
            classes={classes}
          />
          <StatRow
            label="RTT"
            value={formatValue(stats.rttMs, ' ms')}
            classes={classes}
          />
          <StatRow
            label="Packet loss"
            value={formatValue(stats.packetsLost)}
            classes={classes}
          />
          <StatRow
            label="Jitter"
            value={formatValue(stats.jitterMs, ' ms')}
            classes={classes}
          />
          <StatRow
            label="Freeze count"
            value={formatValue(stats.freezeCount)}
            classes={classes}
          />
          <StatRow
            label="Freeze time"
            value={formatValue(stats.totalFreezeDurationMs, ' ms')}
            classes={classes}
          />
          <StatRow
            label="Encoder QP"
            value={formatValue(stats.qp)}
            classes={classes}
          />
          <StatRow label="Codec" value={formatValue(stats.codec)} classes={classes} />
        </Box>
      </Box>

      <Box sx={classes.section}>
        <Typography sx={classes.sectionTitle}>Bitrate (kbps)</Typography>
        <Box sx={classes.controlRow}>
          <TextField
            sx={classes.numberInput}
            size="small"
            type="number"
            label="Min"
            value={minBitrate}
            onChange={(e) => setMinBitrate(e.target.value)}
            inputProps={{ min: 0 }}
          />
          <TextField
            sx={classes.numberInput}
            size="small"
            type="number"
            label="Max"
            value={maxBitrate}
            onChange={(e) => setMaxBitrate(e.target.value)}
            inputProps={{ min: 0 }}
          />
          <Button variant="contained" onClick={applyBitrate}>
            Apply
          </Button>
        </Box>
        <Typography sx={classes.hint}>Applies immediately, no reconnect needed.</Typography>
      </Box>

      <Box sx={classes.section}>
        <Typography sx={classes.sectionTitle}>Codec</Typography>
        <Box sx={classes.controlRow}>
          <Select
            sx={classes.select}
            size="small"
            value={selectedCodec}
            onChange={(e) => handleCodecChange(e.target.value as string)}
          >
            {codecOptions.map((codec) => (
              <MenuItem key={codec} value={codec}>
                {codec}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Typography sx={classes.hint}>
          Applies on the next Reconnect, not to the live connection.
        </Typography>
      </Box>

      <Box sx={classes.section}>
        <Typography sx={classes.sectionTitle}>Actions</Typography>
        <Box sx={classes.controlRow}>
          <Button
            variant="outlined"
            onClick={() => mytaPixelStreaming.requestIframe()}
          >
            Force Keyframe
          </Button>
          <Button
            variant="outlined"
            onClick={() => mytaPixelStreaming.reconnect()}
          >
            Reconnect
          </Button>
          <Button
            variant="outlined"
            onClick={() => mytaPixelStreaming.requestShowFps()}
          >
            Toggle FPS Overlay
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default PixelStreamingTab;
